export const ZONES = [
  { key: 'main_large', name: 'Большой блок' },
  { key: 'left_vertical', name: 'Левый вертикальный' },
  ...[1, 2, 3, 4].map(n => ({ key: `right_${n}`, name: `Правый ${n}` })),
];

export const DEFAULT_LAYOUT = {
  base_width: 2160, base_height: 3840, name: 'Вертикальный · 6 зон',
  zones: [
    { role: 'clock', x: 40, y: 40, w: 560, h: 430 },
    { role: 'weather', x: 640, y: 40, w: 1010, h: 430 },
    { role: 'qr', x: 1690, y: 40, w: 430, h: 430 },
    { role: 'media', ref: 'main_large', x: 40, y: 510, w: 2080, h: 1170 },
    { role: 'media', ref: 'left_vertical', x: 40, y: 1720, w: 1170, h: 2080 },
    ...[1, 2, 3, 4].map((n, i) => ({ role: 'media', ref: `right_${n}`, x: 1250, y: 1720 + i * 530, w: 870, h: 490 })),
  ],
};

export const clone = value => JSON.parse(JSON.stringify(value));
export const zoneName = key => ZONES.find(z => z.key === key)?.name || key;
export const PROGRAM_MIN_VERSION = '0.5.0';

export function versionTuple(value) {
  const parts = String(value || '').match(/\d+/g)?.slice(0, 3).map(Number) || [];
  return [...parts, 0, 0, 0].slice(0, 3);
}

export function versionAtLeast(value, minimum = PROGRAM_MIN_VERSION) {
  const current = versionTuple(value), required = versionTuple(minimum);
  for (let index = 0; index < required.length; index += 1) {
    if (current[index] !== required[index]) return current[index] > required[index];
  }
  return true;
}

export const supportsPrograms = device => versionAtLeast(device?.app_version);

// API timestamps are UTC but older responses omit the timezone suffix.
export function parseDate(value) {
  if (!value) return null;
  const raw = String(value);
  const date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  return parseDate(value)?.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) || 'Нет данных';
}

export function isOnline(device, now = Date.now()) {
  const date = parseDate(device?.last_seen_at);
  return !!date && now - date.getTime() <= 90000;
}

export function formatBytes(value) {
  let size = Number(value || 0), unit = 0;
  while (size >= 1024 && unit < 3) { size /= 1024; unit += 1; }
  return `${size.toFixed(size >= 10 || !unit ? 0 : 1)} ${['Б', 'КБ', 'МБ', 'ГБ'][unit]}`;
}

export function formatDuration(value) {
  let seconds = Math.max(0, Number(value || 0));
  const hours = Math.floor(seconds / 3600); seconds %= 3600;
  const minutes = Math.floor(seconds / 60); seconds %= 60;
  return [hours && `${hours} ч`, minutes && `${minutes} мин`, (seconds || (!hours && !minutes)) && `${seconds} с`].filter(Boolean).join(' ');
}

export function playlistDuration(playlist, mediaById) {
  return (playlist || []).reduce((sum, item) => {
    const media = mediaById[item.media_id];
    return sum + (media?.media_type === 'video' ? Number(media.duration || 0) : Number(item.duration || 6));
  }, 0);
}

export function playlistSize(playlist, mediaById) {
  return (playlist || []).reduce((sum, item) => sum + Number(mediaById[item.media_id]?.size || 0), 0);
}

export function devicePayload(form) {
  return {
    device_key: form.device_key, name: form.name, location: form.location || '',
    is_active: !!form.is_active, rotation: Number(form.rotation ?? 270),
    background_media_id: form.background_media_id ? Number(form.background_media_id) : null,
    ticker: form.ticker || '', qr_data: form.qr_data || '',
    weather_name: form.weather_name || '', weather_lat: String(form.weather_lat ?? ''), weather_lon: String(form.weather_lon ?? ''),
    zone_playlists: form.zone_playlists || {},
    // Canonical zone playlists replace the historical fallback playlists on an explicit save.
    main_playlist: [], promo_playlist: [],
  };
}

export function snapshot(value) {
  return JSON.stringify(value, (key, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(k => [k, item[k]])) : item);
}

export function moveItem(list, from, to) {
  if (![from, to].every(i => Number.isInteger(i) && i >= 0 && i < list.length)) return list;
  const next = [...list];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export function mediaUsage(id, devices, contentSets, programs = []) {
  const uses = value => Number(value.background_media_id) === Number(id)
    || Object.values(value.zone_playlists || {}).some(list => list.some(item => Number(item.media_id) === Number(id)));
  const sets = contentSets.filter(uses);
  const programIds = programs.filter(program => (program.scenes || []).some(scene => sets.some(set => set.id === Number(scene.content_set_id)))).map(program => program.id);
  const screens = devices.filter(d => uses(d) || sets.some(s => s.id === d.content_set_id) || programIds.includes(d.program_id));
  return { sets, screens, programs: programs.filter(item => programIds.includes(item.id)), used: !!(sets.length || screens.length) };
}

export function validateDevice(form, mediaById) {
  if (!form.name?.trim()) return 'Укажите название экрана.';
  if (!/^[a-zA-Z0-9_-]+$/.test(form.device_key || '')) return 'Ключ экрана: латинские буквы, цифры, дефис или подчёркивание.';
  if (!String(form.weather_lat).trim() || !Number.isFinite(Number(form.weather_lat)) || Math.abs(Number(form.weather_lat)) > 90) return 'Широта должна быть от −90 до 90.';
  if (!String(form.weather_lon).trim() || !Number.isFinite(Number(form.weather_lon)) || Math.abs(Number(form.weather_lon)) > 180) return 'Долгота должна быть от −180 до 180.';
  return validateContent(form, mediaById);
}

export function validateContent(form, mediaById) {
  if (!form.name?.trim()) return 'Укажите название.';
  if (form.background_media_id && mediaById[form.background_media_id]?.media_type !== 'image') return 'Фоновое изображение не найдено. Выберите другой фон.';
  for (const [key, list] of Object.entries(form.zone_playlists || {})) {
    for (const item of list) {
      if (!mediaById[item.media_id]) return `${zoneName(key)}: файл #${item.media_id} не найден. Уберите его из плейлиста.`;
      if (mediaById[item.media_id].media_type !== 'video' && (!Number.isFinite(Number(item.duration ?? 6)) || Number(item.duration ?? 6) < 1 || Number(item.duration ?? 6) > 120)) return `${zoneName(key)}: длительность должна быть от 1 до 120 секунд.`;
    }
  }
  return '';
}

export function validateLayout(draft) {
  if (!draft.name?.trim()) return 'Укажите название раскладки.';
  if (![draft.base_width, draft.base_height].every(n => Number.isInteger(n) && n >= 320)) return 'Размеры экрана должны быть целыми и не меньше 320 px.';
  if (!draft.zones?.length) return 'Добавьте хотя бы одну зону.';
  const refs = new Set();
  for (const [i, z] of draft.zones.entries()) {
    if (![z.x, z.y, z.w, z.h].every(Number.isInteger) || z.x < 0 || z.y < 0 || z.w <= 0 || z.h <= 0 || z.x + z.w > draft.base_width || z.y + z.h > draft.base_height) return `Зона ${i + 1}: проверьте размеры и границы экрана.`;
    if (z.role === 'media') {
      if (!ZONES.some(item => item.key === z.ref)) return `Зона ${i + 1}: API поддерживает только шесть стандартных ключей медиа.`;
      if (refs.has(z.ref)) return `Зона «${zoneName(z.ref)}» повторяется.`;
      refs.add(z.ref);
    }
  }
  return '';
}

export function layoutWarnings(draft) {
  const warnings = [];
  for (let i = 0; i < (draft.zones || []).length; i += 1) {
    for (let j = i + 1; j < draft.zones.length; j += 1) {
      const a = draft.zones[i], b = draft.zones[j];
      if (a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y) warnings.push(`Зоны ${i + 1} и ${j + 1} пересекаются.`);
    }
  }
  return warnings;
}

export function validateProgram(draft, layouts, contentSets) {
  if (!draft.name?.trim()) return 'Укажите название сценария.';
  if (!draft.scenes?.length) return 'Добавьте хотя бы одну сцену.';
  if (draft.scenes.length > 100) return 'В сценарии может быть не больше 100 сцен.';
  for (const [index, scene] of draft.scenes.entries()) {
    const duration = Number(scene.duration);
    if (!Number.isInteger(duration) || duration < 5 || duration > 86400) return `Сцена ${index + 1}: длительность должна быть от 5 секунд до 24 часов.`;
    if (scene.layout_id && !layouts.some(item => item.id === Number(scene.layout_id) && item.is_active)) return `Сцена ${index + 1}: раскладка недоступна.`;
    if (scene.content_set_id && !contentSets.some(item => item.id === Number(scene.content_set_id))) return `Сцена ${index + 1}: набор контента недоступен.`;
    if (!['cut', 'fade'].includes(scene.transition || 'cut')) return `Сцена ${index + 1}: неизвестный переход.`;
  }
  return '';
}

export function syncState(device) {
  if (device?.last_error) return { tone: 'bad', label: 'Ошибка синхронизации' };
  const request = parseDate(device?.force_reload_at);
  const success = parseDate(device?.last_sync_at);
  if (request && (!success || success < request)) return { tone: 'wait', label: 'Ожидает подтверждения ТВ' };
  if (!supportsPrograms(device) && device?.applied_config_version == null) {
    const version = device?.app_version || 'без версии';
    return { tone: success ? 'ok' : 'muted', label: success ? `Legacy ${version} · синхронизация успешна` : `Legacy ${version} · без отчёта версии` };
  }
  if (Number(device?.applied_config_version || 0) < Number(device?.config_version || 0)) return { tone: 'wait', label: 'Изменения ещё не применены на ТВ' };
  return { tone: success ? 'ok' : 'muted', label: success ? 'Последняя синхронизация успешна' : 'Синхронизации ещё не было' };
}
